// App.js
import React from "react";
import useDeploymentVersion from "./hooks/useDeploymentVersion";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { setNavigate } from "./utils/navigation";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
  Box,
} from "@mui/material";
import { Provider, useSelector } from "react-redux";
import {
  QueryClient,
  QueryClientProvider,
  useIsFetching,
} from "@tanstack/react-query";
import { UserProvider } from "./context/UserContext";
import { ToastProvider } from "./comp/ToastProvider";
import { DialogProvider } from "./comp/DialogProvider";
import AppRoutes from "./routes/AppRoutes";
import NavigationSetter from "./routes/NavigationSetter";
import AppLayout from "./routes/AppLayout";
import store from "./store";

import redImg from "./assets/images/red-blur.png";
import cyanImg from "./assets/images/cyan-blur.png";

import { LoadingProvider } from "./context/LoadingContext";
import LoadingOverlay from "./comp/LoadingOverlay";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/ko";

// MUI 테마 설정
const muiTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#1890ff" },
    secondary: { main: "#00B8D9" },
    error: { main: "#FF5630" },
    warning: { main: "#FFAB00" },
    success: { main: "#22C55E" },
    info: { main: "#00B8D9" },
  },
  typography: {
    fontFamily: `'Pretendard', 'Apple SD Gothic Neo', 'NanumGothic', 'Segoe UI', '-apple-system', 'Roboto', 'Arial', sans-serif`,
    fontSize: 12, // 전체 rem 기준
    button: {
      fontSize: "0.875rem", // 약 14px
    },
  },
  shape: {
    borderRadius: 3,
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          padding: "6px 12px",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          // input 내 텍스트 영역 높이 맞추기 위해 lineHeight, padding 조절 가능
          "& input": {
            height: "32px",
            boxSizing: "border-box",
            padding: "4 8px",
          },
        },
      },
    },
    /* ===============================
    * Select 전역 스타일 (추가)
    * =============================== */
    MuiSelect: {
      styleOverrides: {
        select: {
          display: "flex",
          alignItems: "center",
          height: 40,
          padding: "10px 8px",
          fontSize: "11px",
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderWidth: "1px", // 기본
        },
        root: {
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px", // 포커스 시 두께
          },
        },
      },
    },    
    /*
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: 40,
          fontSize: "11px",

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0,0,0,0.23)",
          },

          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1890ff",
          },

          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#1890ff",
            borderWidth: 2,
          },

          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "#FF5630",
          },
        },
      },
    },    
    */

    MuiMenu: {
      styleOverrides: {
        paper: {
          marginTop: 4,
          borderRadius: 12,
          border: "1px solid rgba(0,0,0,0.12)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          maxHeight: 240,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "11px",
          minHeight: 32,
          padding: "6px 12px",

          "&:not(:last-of-type)": {
            borderBottom: "1px solid rgba(0,0,0,0.08)",
          },

          "&:hover": {
            backgroundColor: "rgba(24,144,255,0.08)",
          },

          "&.Mui-selected": {
            backgroundColor: "#1890ff",
            color: "#fff",
            fontWeight: 600,
          },

          "&.Mui-selected:hover": {
            backgroundColor: "#1577d3",
          },
        },
      },
    },    
    MuiPickersCalendarHeader: {
      defaultProps: {
        format: "YYYY년 M월",
      },
    },        

    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontWeight: 600,

          "&:first-of-type": {
            color: "#d32f2f", // 일요일
          },
          "&:last-of-type": {
            color: "#1976d2", // 토요일
          },
        },
      },
    },  

    MuiPickersDay: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.day?.day() === 0 && {
            color: "#d32f2f", // 일요일
          }),
          ...(ownerState.day?.day() === 6 && {
            color: "#1976d2", // 토요일
          }),
        }),
      },
    },  
  },
});

// 전역 스타일 정의
const globalStyles = (
  <GlobalStyles
    styles={{
      body: {
        margin: 0,
        fontFamily: `'Pretendard', 'Noto Sans KR', sans-serif, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen'`,
        backgroundColor: "#fff",
        color: "#212b36",
        backgroundImage: `url(${redImg}), url(${cyanImg})`,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "top right, bottom left",
        backgroundSize: "200px 200px, 200px 200px",
      },
      a: {
        color: "#1890ff",
        textDecoration: "none",
        "&:hover": {
          textDecoration: "underline",
        },
      },
    }}
  />
);

// 전역 로딩 스피너 컴포넌트 (React Query의 fetch 상태 기반)
const GlobalLoading = () => {
  const isFetching = useIsFetching();

  if (!isFetching) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: 4,
        bgcolor: "primary.main",
        zIndex: 1300,
        animation: "loading-bar 2s linear infinite",
      }}
    />
  );
};

const queryClient = new QueryClient();

const DeploymentVersionWatcher = () => {
  useDeploymentVersion();
  return null;
};

const App = () => (
  <Provider store={store}>
    <QueryClientProvider client={queryClient}>
      <MuiThemeProvider theme={muiTheme}>
        <DeploymentVersionWatcher />
        <CssBaseline />
        {globalStyles}

        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="ko"
        >
         
          <LoadingProvider>

            <UserProvider>
              <DialogProvider>
                <LoadingOverlay />
                <BrowserRouter>
                  <AppLayout />
                  <NavigationSetter />
                  <AppRoutes />
                </BrowserRouter>
              </DialogProvider>
            </UserProvider>

            <ToastProvider />
          </LoadingProvider>
        </LocalizationProvider>   
      </MuiThemeProvider>
    </QueryClientProvider>
  </Provider>
);

export default App;
